import { Download, Edit2, Eye, Search, UserIcon } from "lucide-react";
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AppButton from "../../components/ui/AppButton";
import Pagination from "../../components/ui/dashboard/Pagination";
import Table, { Column } from "../../components/ui/dashboard/Table";
import { MySelect } from "../../components/ui/Select";
import ThreeDotsMenu from "../../components/ui/ThreeDotsMenu";
import { buildApiUrl, cn } from "../../lib/utils";
import { Company, CompanySearchRequest, CompanySearchResponse } from "./@types";
import { useGetCompaniesMutation } from "./companyApiSlice";
import ExpenseDashCardView from "./ui/CompanyDashCard";

const CompanyDashboard: React.FC = () => {
  const navigate = useNavigate();
  // State management
  const [searchTerm, setSearchTerm] = useState("");
  const [assetTypeFilter, setAssetTypeFilter] = useState("");
  const [assetStatusFilter, setAssetStatusFilter] = useState("");
  const [dateAddedFilter, setDateAddedFilter] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  // RTK Query mutation
  const [getCompanies, { data: vehicleData, isLoading }] =
    useGetCompaniesMutation();

  const fetchCompanies = useCallback(async () => {
    const requestBody: CompanySearchRequest = {
      pageNumber: currentPage,
      pageSize: pageSize,
      keyword: searchTerm || undefined,
      orderBy: ["CreatedOn"],
    };

    try {
      await getCompanies(requestBody).unwrap();
    } catch (err) {
      console.error("Failed to fetch companies:", err);
    }
  }, [
    currentPage,
    pageSize,
    searchTerm,
    assetTypeFilter,
    assetStatusFilter,
    getCompanies,
  ]);

  // Fetch companies on component mount and when filters change
  useEffect(() => {
    fetchCompanies();
  }, [
    currentPage,
    pageSize,
    searchTerm,
    assetTypeFilter,
    assetStatusFilter,
    dateAddedFilter,
    fetchCompanies,
  ]);

  // Table columns configuration
  const columns: Column<Company>[] = [
    {
      key: "userName",
      header: "Company name",
      width: "",
      render: (value: string, item: Company) => (
        <div className="flex items-center space-x-3">
          {item.imageUrl ? (
            <img
              src={buildApiUrl(item.imageUrl)}
              alt={`${item.fullName}`}
              className="w-[30px] h-[30px] rounded-full object-cover border border-gray-300 shadow-sm"
            />
          ) : (
            <div className="w-[30px] h-[30px] rounded-full bg-gray-200 flex items-center justify-center border border-gray-300 shadow-sm p-1">
              <UserIcon className="w-full h-full text-hColor" />
            </div>
          )}
          <div className="flex flex-col">
            <span className="text-hColor capitalize">{item.fullName}</span>
            <span className="text-hColor text-sm">{item.email}</span>
          </div>
        </div>
      ),
    },
    {
      key: "contatcPerson",
      header: "Contact person",
      width: "24",
      render: (value: string) => (
        <span className="text-hColor capitalize">{value}</span>
      ),
    },
    {
      key: "totalVehicles",
      header: "Vehicles",
      width: "20",
      render: (value: string) => <span className="text-hColor">{value}</span>,
    },
    {
      key: "totalDrivers",
      header: "Drivers",
      width: "20",
      render: (value: string) => <span className="text-hColor">{value}</span>,
    },
    {
      key: "totalPartners",
      header: "Partners",
      width: "16",
      render: (value: string) => <span className="text-hColor">{value}</span>,
    },
    {
      key: "subscriptionType",
      header: "Subscription",
      width: "16",
      render: (value: string) => <span className="text-hColor">{value}</span>,
    },
    {
      key: "isActive",
      header: "Status",
      width: "20",
      render: (value: boolean, item: Company) => {
        const getStatusInfo = () => {
          if (!value)
            return {
              label: "Inactive",
              color: "text-gray-200 border border-gray-200",
            };
          if (item.isActive)
            return {
              label: "Active",
              color: "text-green-800 border border-green-800",
            };
          return {
            label: "Active",
            color: "text-green-800 border border-green-800",
          };
        };

        const status = getStatusInfo();
        return (
          <span
            className={`w-[60px] inline-flex items-center justify-center px-2 py-1 text-xs font-semibold rounded-full ${status.color}`}
          >
            {status.label}
          </span>
        );
      },
    },
    {
      key: "actions",
      header: "",
      width: "12",
      align: "center" as const,
      render: (value: any, item: Company) => {
        return (
          <ThreeDotsMenu
            actions={[
              {
                label: "View vehicle",
                onClick: () => navigate(`/admin/vehicle/${item?.userId}`),
                icon: <Eye className="w-4 h-4" />,
              },
              {
                label: "Edit",
                onClick: () => {},
                icon: <Edit2 className="w-4 h-4" />,
              },
            ]}
          />
        );
      },
    },
  ];

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setCurrentPage(0);
  };

  const handleExportCSV = () => {
    console.log("Export CSV functionality");
  };

  const response = vehicleData?.data as CompanySearchResponse | undefined;

  return (
    <div className="min-h-screen py-6 w-full bg-[#F9F9F9] p-4">
      <div className="w-full mx-auto space-y-6">
        {/* Dashboard Cards */}
        <div className="flex flex-wrap gap-x-4 gap-y-4 sm:gap-x-10 sm:gap-y-6 p-4">
          <ExpenseDashCardView />
        </div>

        {/* All companies section */}
        <div className="space-y-6 bg-white rounded-[10px] p-4">
          <div className="">
            <h2 className="text-xl font-semibold text-hColor">All companies</h2>
          </div>
          {/* Filters and Actions */}
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div className="flex flex-col sm:flex-row gap-3">
              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <input
                  type="text"
                  placeholder="Search"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="text-[16px] pl-9 pr-4 py-2 border border-[#F6F6F6] bg-[#F6F6F6] rounded-[10px] focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary w-full sm:w-[367px] h-[42px]"
                />
              </div>

              {/* Filters */}
              <div className="flex gap-3">
                <div className="relative">
                  <MySelect<string>
                    name={"name"}
                    id={"id"}
                    label={""}
                    options={[
                      "Today",
                      "This week",
                      "This month",
                      "This year",
                      "All time",
                    ]}
                    getValue={(option: string) => option}
                    getName={(option: string) => option}
                    value={dateAddedFilter}
                    onChange={(e) => setDateAddedFilter(e)}
                    onValChange={(e) => setDateAddedFilter(e)}
                    placeholder={"Date added"}
                    disabled={isLoading}
                    className={cn(
                      "focus:ring-primary focus:border-primary",
                      {
                        "opacity-50": isLoading,
                      },
                      "w-[130px] h-[42px] text-[13px] px-2 text-textColor appearance-none bg-white border border-[#CECECE] rounded-lg focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary"
                    )}
                  />
                </div>
              </div>
            </div>

            <div className="flex gap-3">
              <AppButton
                appearance="soft"
                onClick={handleExportCSV}
                className="flex items-center px-4 py-2 border border-primary bg-white rounded-lg text-primary hover:bg-gray-50 transition-colors min-w-[146px] h-[42px]"
              >
                <Download className="h-4 w-4 mr-2" />
                Export csv
              </AppButton>
            </div>
          </div>

          {/* Table */}
          <div className="overflow-hidden space-y-6">
            <Table
              data={response?.data || []}
              columns={columns}
              loading={isLoading}
              emptyMessage="No companies found"
            />

            {/* Pagination */}
            {response && (
              <Pagination
                currentPage={response.currentPage}
                totalPages={response.totalPages}
                pageSize={response.pageSize}
                totalCount={response.totalCount}
                hasNextPage={response.hasNextPage}
                hasPreviousPage={response.hasPreviousPage}
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompanyDashboard;
